<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import MessageItem from './MessageItem.vue';
import SearchPanel from './SearchPanel.vue';
import { channelColor, channelDisplayName } from '../helpers/timeline';

const props = defineProps({
  channel: { type: Object, required: true },
  tab: { type: String, required: true },
  // [{ id, name, thumbnail, role }] — members, or the whole team if public.
  people: { type: Array, default: () => [] },
  // Agents that can still be added (private channels).
  candidates: { type: Array, default: () => [] },
  canManageMembers: { type: Boolean, default: false },
  canManageChannel: { type: Boolean, default: false },
  currentUserId: { type: Number, default: null },
  statusFor: { type: Function, required: true },
  nameFor: { type: Function, required: true },
  // Bumped by the page after a pin/star so the lists here refetch.
  refreshKey: { type: Number, default: 0 },
});

const emit = defineEmits([
  'close',
  'changeTab',
  'edit',
  'addMembers',
  'removeMember',
  'toggleRole',
  'openResult',
]);

const { t } = useI18n();
const store = useStore();

const TABS = [
  { key: 'about', label: 'TEAM_CHAT.ABOUT' },
  { key: 'members', label: 'TEAM_CHAT.MEMBERS' },
  { key: 'pinned', label: 'TEAM_CHAT.PINNED' },
  { key: 'starred', label: 'TEAM_CHAT.STARRED' },
];

const filtered = ref([]);
const isLoading = ref(false);
const search = ref('');
const selected = ref([]);

const loadFiltered = async () => {
  if (props.tab !== 'pinned' && props.tab !== 'starred') return;
  isLoading.value = true;
  try {
    filtered.value = await store.dispatch('teamChat/fetchFilteredMessages', {
      channelId: props.channel.id,
      filter: props.tab,
    });
  } catch {
    filtered.value = [];
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => [props.tab, props.channel.id, props.refreshKey],
  loadFiltered,
  { immediate: true }
);
watch(
  () => props.channel.id,
  () => {
    search.value = '';
    selected.value = [];
  }
);

const matches = person =>
  (person.name || '').toLowerCase().includes(search.value.trim().toLowerCase());
const visiblePeople = computed(() => props.people.filter(matches));
const visibleCandidates = computed(() => props.candidates.filter(matches));

const addSelected = () => {
  emit('addMembers', selected.value);
  selected.value = [];
};
</script>

<template>
  <aside
    class="flex flex-col h-full border-s border-n-weak bg-n-solid-1 w-full lg:w-80 lg:flex-shrink-0"
  >
    <header
      class="flex items-center justify-between flex-shrink-0 h-16 px-4 border-b border-n-weak"
    >
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ tab === 'search' ? t('TEAM_CHAT.SEARCH') : t('TEAM_CHAT.INFO') }}
      </h2>
      <button
        type="button"
        class="flex items-center justify-center rounded-lg size-8 text-n-slate-11 hover:bg-n-alpha-2"
        :title="t('TEAM_CHAT.CLOSE')"
        @click="emit('close')"
      >
        <span class="size-4 i-lucide-x" />
      </button>
    </header>

    <SearchPanel
      v-if="tab === 'search'"
      class="flex-1"
      :channel="channel"
      :current-user-id="currentUserId"
      :status-for="statusFor"
      :name-for="nameFor"
      @open="emit('openResult', $event)"
    />
    <nav v-else class="flex flex-shrink-0 gap-1 px-3 pt-3">
      <button
        v-for="item in TABS"
        :key="item.key"
        type="button"
        class="px-2.5 py-1.5 text-[0.8125rem] rounded-lg"
        :class="
          tab === item.key
            ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
            : 'text-n-slate-11 hover:bg-n-alpha-1'
        "
        @click="emit('changeTab', item.key)"
      >
        {{ t(item.label) }}
      </button>
    </nav>

    <div v-if="tab !== 'search'" class="flex-1 min-h-0 p-4 overflow-y-auto">
      <template v-if="tab === 'about'">
        <div class="flex flex-col items-center pt-2 text-center">
          <span
            class="flex items-center justify-center rounded-2xl size-16"
            :class="channelColor(channel)"
          >
            <span
              class="size-7"
              :class="channel.private ? 'i-lucide-lock' : 'i-lucide-hash'"
            />
          </span>
          <p class="mt-3 font-semibold text-n-slate-12">
            {{ channelDisplayName(channel) }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{ channel.private ? t('TEAM_CHAT.PRIVATE') : t('TEAM_CHAT.PUBLIC') }}
            · {{ t('TEAM_CHAT.MEMBER_COUNT', { n: people.length }, people.length) }}
          </p>
        </div>
        <section class="mt-6 space-y-4 text-sm">
          <div>
            <h3
              class="mb-1 text-[0.6875rem] font-semibold tracking-wide uppercase text-n-slate-10"
            >
              {{ t('TEAM_CHAT.ABOUT') }}
            </h3>
            <p
              class="whitespace-pre-wrap"
              :class="channel.description ? 'text-n-slate-12' : 'text-n-slate-10'"
            >
              {{ channel.description || t('TEAM_CHAT.NO_DESCRIPTION') }}
            </p>
          </div>
          <div v-if="channel.announcement">
            <h3
              class="mb-1 text-[0.6875rem] font-semibold tracking-wide uppercase text-n-slate-10"
            >
              {{ t('TEAM_CHAT.ANNOUNCEMENT') }}
            </h3>
            <p class="whitespace-pre-wrap text-n-slate-12">
              {{ channel.announcement }}
            </p>
          </div>
          <p
            v-if="channel.read_only"
            class="flex items-center gap-2 px-3 py-2 text-xs rounded-lg bg-n-alpha-1 text-n-slate-11"
          >
            <span class="flex-shrink-0 size-4 i-lucide-megaphone" />
            {{ t('TEAM_CHAT.READ_ONLY_HINT') }}
          </p>
          <Button
            v-if="canManageChannel"
            :label="t('TEAM_CHAT.EDIT')"
            icon="i-lucide-pencil"
            variant="outline"
            color="slate"
            size="sm"
            class="w-full"
            @click="emit('edit')"
          />
        </section>
      </template>

      <template v-else-if="tab === 'members'">
        <label
          class="flex items-center gap-2 px-3 mb-3 rounded-lg h-9 bg-n-alpha-2 text-n-slate-10"
        >
          <span class="flex-shrink-0 size-4 i-lucide-search" />
          <input
            v-model="search"
            type="search"
            class="w-full !m-0 !p-0 text-sm !bg-transparent !border-0 !outline-none !shadow-none text-n-slate-12 placeholder:text-n-slate-10"
            :placeholder="t('TEAM_CHAT.SEARCH_PEOPLE')"
          />
        </label>
        <p
          v-if="!visiblePeople.length && !visibleCandidates.length"
          class="py-4 text-sm text-center text-n-slate-10"
        >
          {{ t('TEAM_CHAT.NO_PEOPLE_FOUND') }}
        </p>
        <div
          v-for="person in visiblePeople"
          :key="person.id"
          class="flex items-center gap-2.5 py-1.5 group"
        >
          <Avatar
            :src="person.thumbnail"
            :name="person.name || '?'"
            :status="statusFor(person.id)"
            :size="32"
            rounded-full
          />
          <span class="flex-1 min-w-0 text-sm truncate text-n-slate-12">
            {{ person.name }}
            <span v-if="person.id === currentUserId" class="text-n-slate-10">
              ({{ t('TEAM_CHAT.YOU') }})
            </span>
          </span>
          <span
            v-if="person.role === 'admin'"
            class="px-1.5 py-0.5 text-[0.6875rem] font-medium rounded bg-n-blue-3 text-n-blue-11"
          >
            {{ t('TEAM_CHAT.ADMIN') }}
          </span>
          <div
            v-if="canManageMembers && channel.private && person.id !== currentUserId"
            class="items-center hidden gap-0.5 group-hover:flex"
          >
            <button
              type="button"
              class="flex items-center justify-center rounded-md size-7 text-n-slate-11 hover:bg-n-alpha-2"
              :title="
                person.role === 'admin'
                  ? t('TEAM_CHAT.MAKE_MEMBER')
                  : t('TEAM_CHAT.MAKE_ADMIN')
              "
              @click="emit('toggleRole', person)"
            >
              <span class="size-3.5 i-lucide-shield" />
            </button>
            <button
              type="button"
              class="flex items-center justify-center rounded-md size-7 text-n-slate-11 hover:bg-n-ruby-3 hover:text-n-ruby-11"
              :title="t('TEAM_CHAT.REMOVE')"
              @click="emit('removeMember', person.id)"
            >
              <span class="size-3.5 i-lucide-user-minus" />
            </button>
          </div>
        </div>

        <template
          v-if="canManageMembers && channel.private && visibleCandidates.length"
        >
          <h3
            class="pt-4 pb-1.5 text-[0.6875rem] font-semibold tracking-wide uppercase text-n-slate-10"
          >
            {{ t('TEAM_CHAT.ADD_MEMBERS') }}
          </h3>
          <label
            v-for="agent in visibleCandidates"
            :key="agent.id"
            class="flex items-center gap-2.5 py-1.5 cursor-pointer"
          >
            <input v-model="selected" type="checkbox" :value="agent.id" />
            <Avatar
              :src="agent.thumbnail"
              :name="agent.name || '?'"
              :size="24"
              rounded-full
            />
            <span class="text-sm truncate text-n-slate-12">{{ agent.name }}</span>
          </label>
          <Button
            :label="t('TEAM_CHAT.ADD')"
            icon="i-lucide-user-plus"
            size="sm"
            class="w-full mt-2"
            :disabled="!selected.length"
            @click="addSelected"
          />
        </template>
      </template>

      <template v-else>
        <div v-if="isLoading" class="flex justify-center py-8">
          <span class="size-5 i-lucide-loader-circle animate-spin text-n-slate-10" />
        </div>
        <div v-else-if="!filtered.length" class="px-2 py-10 text-center">
          <span
            class="inline-flex items-center justify-center mb-3 rounded-full size-12 bg-n-alpha-2 text-n-slate-10"
          >
            <span
              class="size-5"
              :class="tab === 'pinned' ? 'i-lucide-pin' : 'i-lucide-star'"
            />
          </span>
          <p class="text-sm font-medium text-n-slate-12">
            {{
              tab === 'pinned' ? t('TEAM_CHAT.NO_PINNED') : t('TEAM_CHAT.NO_STARRED')
            }}
          </p>
          <p class="mt-1 text-xs text-n-slate-11">
            {{
              tab === 'pinned'
                ? t('TEAM_CHAT.NO_PINNED_HINT')
                : t('TEAM_CHAT.NO_STARRED_HINT')
            }}
          </p>
        </div>
        <div v-else class="space-y-3">
          <div
            v-for="message in filtered"
            :key="message.id"
            class="p-2 border rounded-xl border-n-weak"
          >
            <MessageItem
              :message="message"
              :status="statusFor(message.user_id ?? message.user?.id)"
              :current-user-id="currentUserId"
              :name-for="nameFor"
              compact
            />
          </div>
        </div>
      </template>
    </div>
  </aside>
</template>
